import { default  as AgentComponent } from '../components/AIAgentComp/Agent';
import { PendingFile, UploadedFile } from '../components/AIAgentComp/AgentInput';
import back4app2, { useBack4app2ConnectionStatus } from '../back4app2';
import { 
  Agent, 
  AgentCharLimitExceededError, 
  AgentStatus, 
  AgentStatusError, 
  AgentsSubscriptionChatMessagesHardLimitError, 
  AuthenticationError, 
  AuthorizationError, 
  Back4app2Error,
  BlockedResourceError, 
  ChatMessage, 
  ChatMessageStatus, 
  InvalidInputError, 
  NetworkError, 
  NotFoundError, 
  PaymentPendingError, 
  Task,
  TaskStatus,
} from '@back4app2/sdk';
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import toast from 'react-hot-toast';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from 'react';
import LoadingMessage from '../components/LoadingMessage';
import ErrorMessage from '../components/ErrorMessage';
import { Message, MessageAttachment } from '../types/Agent';
import { pushGTMEvent } from '../utils/gtm';
import { extractLatestAppInfoFromMessages } from '../utils/agentResultParser';
import { isAgentFeatureEnabled } from '../utils/agentFeatures';
import AgentUpgradeModal from '../components/AIAgentComp/AgentUpgradeModal';
import RollbackConfirmModal from '../components/AIAgentComp/RollbackConfirmModal';

type AgentWithOutdatedFlag = Agent & { isOutdated?: boolean };

const DEFAULT_PAGE_SIZE = 20;

const PAGE_SIZE = (() => {
  const raw = Number(process.env.REACT_APP_BACK4APP2_DASHBOARD_AGENT_CHAT_PAGE_SIZE);
  const value = Number.isFinite(raw) ? Math.trunc(raw) : DEFAULT_PAGE_SIZE;
  return Math.max(1, Math.min(value, 200)); // backend cap
})();

interface MyAgentState {
  isLoading: boolean;
  loadingErrorMessage?: string;
  messages?: ChatMessage[];
  paymentErrorMessage?: string;

  // pagination
  hasMoreOlder: boolean;
  nextBefore?: Date;
  isLoadingOlder: boolean;
}

const INITIAL_STATE: MyAgentState = {
  isLoading: true,
  loadingErrorMessage: undefined,
  messages: undefined,
  paymentErrorMessage: undefined,

  hasMoreOlder: false,
  nextBefore: undefined,
  isLoadingOlder: false,
};

enum MyAgentActionType {
  RESET,
  FINISH_LOADING,
  FINISH_SENDING,
  SET_PAYMENT_ERROR_MESSAGE,

  SET_LOADING_OLDER,
  PREPEND_OLDER_MESSAGES,
  UPSERT_MESSAGE,
  REMOVE_MESSAGE,
}

const reset = () => ({
  type: MyAgentActionType.RESET
} as const);

const finishLoading = (
  errorMessage?: string, 
  messages?: ChatMessage[],
  pagination?: { hasMoreOlder: boolean; nextBefore?: Date }
) => ({
  type: MyAgentActionType.FINISH_LOADING,
  payload: {
    errorMessage,
    messages,
    pagination,
  }
} as const);

const setLoadingOlder = (value: boolean) => ({
  type: MyAgentActionType.SET_LOADING_OLDER,
  payload: { value },
} as const);

const prependOlderMessages = (items: ChatMessage[], pagination: { hasMoreOlder: boolean; nextBefore?: Date }) => ({
  type: MyAgentActionType.PREPEND_OLDER_MESSAGES,
  payload: { items, pagination },
} as const);

const upsertMessage = (message: ChatMessage) => ({
  type: MyAgentActionType.UPSERT_MESSAGE,
  payload: { message },
} as const);

const removeMessage = (id: string) => ({
  type: MyAgentActionType.REMOVE_MESSAGE,
  payload: { id },
} as const);

const finishSending = (message: ChatMessage) => ({
  type: MyAgentActionType.FINISH_SENDING,
  payload: {
    message
  }
} as const);

const setPaymentErrorMessage = (errorMessage?: string) => ({
  type: MyAgentActionType.SET_PAYMENT_ERROR_MESSAGE,
  payload: {
    errorMessage
  }
} as const);

type MyAgentAction = 
  ReturnType<typeof reset> | 
  ReturnType<typeof finishLoading> | 
  ReturnType<typeof finishSending> | 
  ReturnType<typeof setPaymentErrorMessage> |
  ReturnType<typeof setLoadingOlder> |
  ReturnType<typeof prependOlderMessages> |
  ReturnType<typeof upsertMessage> |
  ReturnType<typeof removeMessage>;

const reducer = (state: MyAgentState = INITIAL_STATE, action: MyAgentAction): MyAgentState => {
  switch (action.type) {
    case MyAgentActionType.RESET:
      return {
        ...INITIAL_STATE
      };
    
    case MyAgentActionType.FINISH_LOADING:
      return {
        ...state,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        messages: (action.payload.messages && [...action.payload.messages]) || undefined,
        hasMoreOlder: action.payload.pagination?.hasMoreOlder ?? state.hasMoreOlder,
        nextBefore: action.payload.pagination?.nextBefore ?? state.nextBefore,
      };

      case MyAgentActionType.SET_LOADING_OLDER:
        return { ...state, isLoadingOlder: action.payload.value };
  
      case MyAgentActionType.PREPEND_OLDER_MESSAGES: {
        const prev = state.messages || [];
        const existingIds = new Set(prev.map((m) => m.id));
        const deduped = action.payload.items.filter((m) => !existingIds.has(m.id));
  
        return {
          ...state,
          messages: [...deduped, ...prev],
          hasMoreOlder: action.payload.pagination.hasMoreOlder,
          nextBefore: action.payload.pagination.nextBefore,
        };
      }
  
      case MyAgentActionType.UPSERT_MESSAGE: {
        const prev = state.messages || [];
        const msg = action.payload.message;
  
        const without = prev.filter((m) => m.id !== msg.id);
        // keep array ASC by askedAt (fallback: keep at end)
        const idx = without.findIndex((m) => {
          const a = m.askedAt ? new Date(m.askedAt).getTime() : Number.POSITIVE_INFINITY;
          const b = msg.askedAt ? new Date(msg.askedAt).getTime() : Number.POSITIVE_INFINITY;
          return a > b;
        });
  
        const next =
          idx === -1
            ? [...without, msg]
            : [...without.slice(0, idx), msg, ...without.slice(idx)];
  
        return { ...state, messages: next };
      }
  
      case MyAgentActionType.REMOVE_MESSAGE:
        return {
          ...state,
          messages: (state.messages || []).filter((m) => m.id !== action.payload.id),
        };
    
    case MyAgentActionType.FINISH_SENDING:
      const messages = [...(state.messages || [])];

      if (!messages.find(message => message.id === action.payload.message.id)) {
        messages.push(action.payload.message);
      }

      return {
        ...state,
        messages
      }

    case MyAgentActionType.SET_PAYMENT_ERROR_MESSAGE:
      return {
        ...state,
        paymentErrorMessage: action.payload.errorMessage
      }
  }
};

const MyAgent = forwardRef<
  { setInputContent: (content: string) => void },
  {
    agentId: string;
    agents: Agent[] | undefined;
    isSidebarOpen: boolean;
    onPreviewUrlChange?: (url?: string) => void;
    onAgentTurnStarted?: () => void; // notify when user sends a message
    onAgentTurnEnded?: (hadDeploy?: boolean) => void; // notify when assistant finishes a response
    onRollbackStarted?: () => void; // notify when rollback starts
    onRollbackCompleted?: () => Promise<void> | void; // notify when rollback is completed
    onRollbackFinished?: () => void; // notify when rollback flow fully settles
    onUpgradePlan?: (priceId: string, frameId?: string) => void;
    onAttachmentClick?: (attachment: MessageAttachment) => void;
    inputContent?: string;
    onInputContentChange?: (value: string) => void;
    pendingFiles?: PendingFile[];
    onPendingFilesChange?: (
      files: PendingFile[] | ((prev: PendingFile[]) => PendingFile[])
    ) => void;
    onSendRequest?: (content: string, uploadedFiles?: UploadedFile[]) => Promise<boolean>;
    /** Callback to open upgrade modal at parent level (full screen) */
    onUpgrade?: () => void;
    scrollToBottomOffsetRem?: number;
  }
>(({ 
  agentId, 
  agents, 
  isSidebarOpen, 
  onPreviewUrlChange, 
  onAgentTurnStarted, 
  onAgentTurnEnded, 
  onRollbackStarted,
  onRollbackCompleted,
  onRollbackFinished,
  onUpgradePlan, 
  onUpgrade, 
  onAttachmentClick, 
  inputContent, 
  onInputContentChange, 
  pendingFiles, 
  onPendingFilesChange, 
  onSendRequest, 
  scrollToBottomOffsetRem,
 }, ref) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const agentRef = useRef<{ setInputContent: (content: string) => void; scrollToBottom: () => void }>(null);

  const { isLoading, loadingErrorMessage, messages, paymentErrorMessage, hasMoreOlder, nextBefore, isLoadingOlder } = state;
  const [isResponding, setIsResponding] = useState(false);
  const [respondingMessageId, setRespondingMessageId] = useState<string | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [rollbackingMessageId, setRollbackingMessageId] = useState<string | null>(null);
  const [pendingRollbackMessageId, setPendingRollbackMessageId] = useState<string | null>(null);
  const [rollbackModalError, setRollbackModalError] = useState<string | null>(null);
  const suppressTurnEndedRef = useRef(false);

  const agent = useMemo<AgentWithOutdatedFlag | undefined>(
    () => (agents && agents.find((agent) => agent.id === agentId)) as AgentWithOutdatedFlag | undefined,
    [agentId, agents]
  );

  const supportsRollback = useMemo(() => {
    return Boolean(agent?.rollbackSupported);
  }, [agent?.rollbackSupported]);

  const isOutdated = useMemo(() => {
    return Boolean(agent?.isOutdated);
  }, [agent?.isOutdated]);

  const showAttachments = useMemo(() => {
    return isAgentFeatureEnabled(agent?.createdAt);
  }, [agent]);

  const chatId = useMemo(() => agent && agent.mainChat && agent.mainChat.id, [agent]);

  const redirectedRef = useRef(false);
  const redirectOnce = (to: string) => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    window.location.replace(to);
  };

  const { isConnected } = useBack4app2ConnectionStatus();
  const wasConnectedRef = useRef(isConnected);

  // keep latest messages in a ref (used by reconnect refetch)
  const messagesRef = useRef<ChatMessage[] | undefined>(undefined);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // block concurrent "load older" calls
  const loadOlderInFlightRef = useRef(false);

  // load older page (called by scroll)
  const loadOlder = useCallback(async () => {
    if (!chatId) return;
    if (!hasMoreOlder) return;
    if (isLoadingOlder) return;
    if (!nextBefore) return;

    if (loadOlderInFlightRef.current) return;
    loadOlderInFlightRef.current = true;

    dispatch(setLoadingOlder(true));
    try {
      const page = await back4app2.findChatMessagesPaginated(chatId, {
        before: nextBefore,
        limit: PAGE_SIZE,
      });

      dispatch(
        prependOlderMessages(page.items || [], {
          hasMoreOlder: !!page.hasMore,
          nextBefore: page.nextBefore,
        })
      );
    } catch (e) {
      // don't break the entire UI on failure to load older messages
      console.error('error loading older chat messages', e);
    } finally {
      loadOlderInFlightRef.current = false;
      dispatch(setLoadingOlder(false));
    }
  }, [chatId, hasMoreOlder, isLoadingOlder, nextBefore]);

  useImperativeHandle(ref, () => ({
    setInputContent: (content: string) => {
      if (agentRef.current) {
        agentRef.current.setInputContent(content);
      }
    },
    // Expose send function for mobile input
    sendMessage: async (content: string, uploadedFiles?: UploadedFile[]): Promise<boolean> => {
      return onSend(content, uploadedFiles);
    },
    // Expose stop function for mobile input
    stopGeneration: async (): Promise<boolean> => {
      return onStop();
    },
    // Expose busy state for mobile input
    get isBusy() {
      return isResponding;
    }
  }));

  useEffect(() => {
    dispatch(reset());
  
    if (!chatId) return;
  
    let cancelled = false;
  
    // Initial page (latest N) via HTTP
    (async () => {
      try {
        const page = await back4app2.findChatMessagesPaginated(chatId, { limit: PAGE_SIZE });
  
        if (cancelled) return;
  
        dispatch(
          finishLoading(undefined, page.items || [], {
            hasMoreOlder: !!page.hasMore,
            nextBefore: page.nextBefore,
          })
        );
      } catch (error) {
        if (cancelled) return;
  
        if (error instanceof NetworkError) {
          console.error('network error', error);
          dispatch(finishLoading('Network error when loading chat messages. Check your internet connection and try again.'));
        } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
          window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
        } else if (error instanceof NotFoundError) {
          dispatch(finishLoading('Chat not found. Reload page, create a new agent or contact our supporting team.'));
        } else {
          console.error('unexpected error loading chat messages', error);
          dispatch(finishLoading('Unexpected error when loading chat messages. Please try again.'));
        }
      }
    })();
  
    // WS events-only (snapshot not needed)
    const subscription = back4app2.subscribeToChatMessageEvents(chatId, (err, ev) => {
      if (err) {
        // don't break the UI if we already have messages; just log
        console.error('chatMessageEvents error', err);
        return;
      }
      if (!ev) return;
  
      switch (ev.kind) {
        case 'INSERT':
        case 'ENTER':
        case 'UPDATE':
          if (ev.object) dispatch(upsertMessage(ev.object));
          break;
        case 'LEAVE':
        case 'DELETE':
          if (ev.id) dispatch(removeMessage(ev.id));
          break;
        case 'ERROR':
        default:
          // optional: refetch the current page
          break;
      }
    });
  
    return () => {
      cancelled = true;
      subscription?.unsubscribe();
      dispatch(reset());
    };
  }, [chatId]);

  useEffect(() => {
    if (!chatId) {
      wasConnectedRef.current = isConnected;
      return;
    }
  
    // transitioned: disconnected -> connected
    if (isConnected && !wasConnectedRef.current) {
      (async () => {
        try {
          const page = await back4app2.findChatMessagesPaginated(chatId, { limit: PAGE_SIZE });
  
          const current = messagesRef.current;
  
          // If we had nothing (or error), bootstrap again
          if (!current || current.length === 0) {
            dispatch(
              finishLoading(undefined, page.items || [], {
                hasMoreOlder: !!page.hasMore,
                nextBefore: page.nextBefore,
              })
            );
            return;
          }
  
          // Otherwise: merge latest items (updates statuses + catches missed inserts)
          (page.items || []).forEach((m) => dispatch(upsertMessage(m)));
        } catch (e) {
          console.error('refetch-on-reconnect failed', e);
        }
      })();
    }
  
    wasConnectedRef.current = isConnected;
  }, [isConnected, chatId]);

  useEffect(() => {
    if (paymentErrorMessage) {
      setIsUpgradeModalOpen(true);
    }
  }, [paymentErrorMessage]);

  const [processingPlanId, setProcessingPlanId] = useState<string | undefined>(undefined);

  const handleUpgradeClose = useCallback(() => {
    setIsUpgradeModalOpen(false);
    setProcessingPlanId(undefined);
    dispatch(setPaymentErrorMessage(undefined));
  }, [dispatch]);

  const handleUpgradeNow = useCallback((priceId: string) => {
    // Paddle is now handled inside AgentUpgradeModal; keep compatibility flag
    setProcessingPlanId(undefined);
  }, []);

  const translatedMessages = useMemo(
    () =>
      messages?.reduce((messages, message): Message[] => {
        const showRollback = supportsRollback;
        const rollbackInProgress = Boolean(rollbackingMessageId);
        const rollbackUnavailable = isResponding || rollbackInProgress;
        const rollbackDisabled =
          !message.cliReleaseObjectId ||
          rollbackUnavailable;
        const rollbackTooltip = isResponding
          ? 'Agent working...'
          : rollbackDisabled
            ? 'Rollback unavailable'
            : 'Rollback to this message';

  
        return [
          ...messages,
          {
            id: `${message.id}-user`,
            role: 'user',
            content: message.question,
            attachments: message.attachments?.map((att) => ({
              id: att.id,
              fileName: att.fileName,
              mimeType: att.mimeType,
              sizeBytes: att.sizeBytes,
              presignedUrl: att.presignedUrl,
            })),
            timestamp: message.askedAt,
          },
          {
            id: `${message.id}-assistant`,
            role: 'assistant',
            content: message.aiResponse || '',
            isTyping: !message.respondedAt,
            hasFailed: message.status === ChatMessageStatus.FAILED,
            error: message.error,
            timestamp: message.respondedAt,
            workedForSeconds: message.askedAt && message.respondedAt
              ? Math.round((message.respondedAt.getTime() - message.askedAt.getTime()) / 1000)
              : undefined,
            chatMessageId: message.id,
            showRollback,
            rollbackDisabled,
            rollbackTooltip,
          },
        ];
      }, [] as Message[]) || undefined,
    [messages, supportsRollback, isResponding, rollbackingMessageId]
  );

  const assistantIsTyping = useMemo(
    () =>
      translatedMessages?.some(
        (message) => message.role === 'assistant' && message.isTyping && !message.hasFailed
      ) || false,
    [translatedMessages]
  );

  // Sync busy state on initial load (handles page refresh while agent is working)
  // This ensures AppPanel stays in loading state after page refresh if agent is still working
  const hasNotifiedInitialBusy = useRef(false);
  useEffect(() => {
    // If assistant is typing when messages first load, notify parent
    if (!hasNotifiedInitialBusy.current && assistantIsTyping && onAgentTurnStarted) {
      hasNotifiedInitialBusy.current = true;
      onAgentTurnStarted();
    }
  }, [assistantIsTyping, onAgentTurnStarted]);

  // Reset flag when assistant stops typing so it can trigger again on next refresh
  useEffect(() => {
    if (!assistantIsTyping) {
      hasNotifiedInitialBusy.current = false;
    }
  }, [assistantIsTyping]);

  useEffect(() => {
    if (assistantIsTyping) {
      setIsResponding(true);
    } else {
      setIsResponding(false);
    }
  }, [assistantIsTyping]);

  // Extract app info and notify parent
  useEffect(() => {
    if (onPreviewUrlChange && translatedMessages) {
      const appInfo = extractLatestAppInfoFromMessages(translatedMessages);
      onPreviewUrlChange(appInfo.url);
    }
  }, [translatedMessages, onPreviewUrlChange]);

  // Notify parent when assistant finishes a response (once per message)
  const lastHandledAssistantId = useRef<string | null>(null);
  useEffect(() => {
    if (!translatedMessages || !onAgentTurnEnded) return;

    const last = [...translatedMessages].reverse().find((m) => m.role === 'assistant');
    if (!last) return;

    const turnEnded = !last.isTyping || !!last.hasFailed;

    if (turnEnded && last.id !== lastHandledAssistantId.current) {
      lastHandledAssistantId.current = last.id;
      setIsResponding(false);
      setRespondingMessageId(null);

      if (suppressTurnEndedRef.current) {
        return;
      }

      const hadDeploy = /`{3,}agent-progress[\s\S]*?(?:"changes"|"changed")[\s\S]*?`{3,}/.test(last.content || '');
      onAgentTurnEnded(hadDeploy);
    }
  }, [translatedMessages, onAgentTurnEnded]);
  // Treat FAILED messages as "turn ended" even if respondedAt was never set (isTyping may stay true)

  const onSend = async (content: string, uploadedFiles?: UploadedFile[]): Promise<boolean> => {
    if (isResponding) return false; // prevent multiple sends while busy
    let message;
    pushGTMEvent('submit_ai_prompt');

    try {
      const attachmentIds = uploadedFiles?.map(f => f.attachmentId) ?? [];
      message = await back4app2.askQuestionToAgent(agentId, content, attachmentIds);
    } catch (e) {
      let errorMessage;
      if (e instanceof NetworkError) {
        console.error('network error', e);
        errorMessage = 'Network error when asking question to agent. Check your internet connection.';
      } else if (
        e instanceof AuthenticationError || 
        e instanceof AuthorizationError ||
        (e instanceof Back4app2Error &&
          (e.code === 'AUTHENTICATION_ERROR' || e.code === 'AUTHORIZATION_ERROR'))
      ) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
        return false;
      } else if (e instanceof AgentStatusError) {
        if (agent && agent.status === AgentStatus.FAILED) {
          errorMessage = 'Sorry, this agent has failed and can not respond to messages. Please create a new agent or contact our supporting team.';
        } else {
          errorMessage = 'This agent is still initializing and can not respond to messages. Please try again later.';
        }
      } else if (e instanceof BlockedResourceError) {
        errorMessage = 'This agent is blocked and can not respond to messages. If you believe that it is an error, please contact our supporting team.';
      } else if (e instanceof PaymentPendingError) {
        errorMessage = 'Please complete the payment process in order to ask questions to the agent.';
        dispatch(setPaymentErrorMessage(errorMessage))
      } else if (e instanceof AgentsSubscriptionChatMessagesHardLimitError) {
        errorMessage = 'Please subscribe to a paying plan in order to continue asking questions to the agent.';
        dispatch(setPaymentErrorMessage(errorMessage))
      } else if (e instanceof AgentCharLimitExceededError) {
        errorMessage = 'Content is too long.';
      } else if (e instanceof NotFoundError || (e as any)?.code === 'NOT_FOUND_ERROR') {
        toast.error(
          'Agent not found. Try again, create a new agent or contact our supporting team.',
          {
            className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-lg text-sm",
            icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
            duration: 6000
          }
        );
        redirectOnce('/agents');
        return false;
      } else {
        console.error('unexpected error asking question to agent', e);
        errorMessage = 'Unexpected error when asking question to agent. Please try again later.';
      }

      toast.error(errorMessage as string, {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-lg text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
      return false;
    }

    dispatch(finishSending(message));
    if (onAgentTurnStarted) onAgentTurnStarted();
    setIsResponding(true);
    // Restore old UX: after user sends a message, always scroll to bottom
    requestAnimationFrame(() => {
      agentRef.current?.scrollToBottom();
    });
    // Track the message ID for stop functionality
    setRespondingMessageId(message.id);
    // isResponding will be cleared when assistant finishes (effect above)
    return true;
  }


  const onRollbackMessage = useCallback(
    async (chatMessageId: string): Promise<boolean> => {
      if (!chatId) return false;
      if (isResponding) return false;
      if (!supportsRollback) return false;
  
      try {
        setRollbackModalError(null);
        setRollbackingMessageId(chatMessageId);
        suppressTurnEndedRef.current = true;
        onRollbackStarted?.();
  
        await back4app2.rollbackAgentMessage(chatMessageId);
  
        const page = await back4app2.findChatMessagesPaginated(chatId, { limit: PAGE_SIZE });
  
        dispatch(
          finishLoading(undefined, page.items || [], {
            hasMoreOlder: !!page.hasMore,
            nextBefore: page.nextBefore,
          })
        );
  
        setPendingRollbackMessageId(null);
        setRollbackModalError(null);
        await onRollbackCompleted?.();
  
        toast.success('Rollback completed successfully.', {
          className:
            'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-lg text-sm',
          duration: 4000,
        });
  
        return true;
      } catch (e) {
        let errorMessage = 'Unexpected error when rolling back. Please try again later.';
  
        if (e instanceof InvalidInputError) {
          errorMessage = e.message;
        } else if (e instanceof NetworkError) {
          errorMessage = 'Network error when rolling back. Check your internet connection.';
        } else if (
          e instanceof AuthenticationError ||
          e instanceof AuthorizationError ||
          (e as any)?.code === 'AUTHENTICATION_ERROR' ||
          (e as any)?.code === 'AUTHORIZATION_ERROR'
        ) {
          window.location.replace(
            `${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(
              window.location.href
            )}`
          );
          return false;
        }

        setRollbackModalError(errorMessage);
  
        toast.error(errorMessage, {
          className:
            'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-lg text-sm',
          duration: 5000,
        });
  
        return false;
      } finally {
        suppressTurnEndedRef.current = false;
        onRollbackFinished?.();
        setRollbackingMessageId(null);
      }
    },
    [chatId, isResponding, supportsRollback, onRollbackStarted, onRollbackCompleted, onRollbackFinished, dispatch]
  );

  const requestRollbackConfirmation = useCallback(
    async (chatMessageId: string): Promise<boolean> => {
      if (isResponding || rollbackingMessageId || !supportsRollback) {
        return false;
      }
  
      setRollbackModalError(null);
      setPendingRollbackMessageId(chatMessageId);
      return true;
    },
    [isResponding, rollbackingMessageId, supportsRollback]
  );

  const closeRollbackModal = useCallback(() => {
    if (rollbackingMessageId) {
      return;
    }

    setPendingRollbackMessageId(null);
    setRollbackModalError(null);
  }, [rollbackingMessageId]);

  const confirmRollback = useCallback(async (): Promise<boolean> => {
    if (!pendingRollbackMessageId) {
      return false;
    }

    return onRollbackMessage(pendingRollbackMessageId);
  }, [pendingRollbackMessageId, onRollbackMessage]);
  

  const onStop = async (): Promise<boolean> => {
    if (!respondingMessageId) return false;
    
    try {
      const task = await back4app2.stopAgentResponse(respondingMessageId);
      
      // If task is null, fast path succeeded immediately - no need to subscribe
      if (!task) {
        return true;
      }
      
      // Slow path - subscribe to task to catch worker failures
      const subscription = back4app2.subscribeToTask(
        task.id,
        (error: Back4app2Error | undefined, updatedTask: Task | undefined) => {
          if (error) {
            toast.error(error.message || 'Failed to stop generation', {
              className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
              icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
              duration: 6000,
            });
            subscription.unsubscribe();
          } else if (updatedTask?.status === TaskStatus.DONE || updatedTask?.status === TaskStatus.FAILED) {
            subscription.unsubscribe();
          }
        }
      );
      
      return true;
    } catch (e) {
      console.error('Error stopping agent response:', e);
      // Re-throw all Back4app2 errors so UI can display appropriate messages
      if (e instanceof Back4app2Error) {
        throw e;
      }
      throw new Back4app2Error('Failed to stop agent response', 'STOP_AGENT_ERROR');
    }
  }

  if (!chatId) {
    return (
      <div className="h-full flex flex-col">
        <LoadingMessage message="Loading agent..." />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <LoadingMessage message="Loading messages..." />
      </div>
    );
  }

  if (loadingErrorMessage) {
    return (
      <div className="h-full flex flex-col">
        <ErrorMessage message={loadingErrorMessage} />
      </div>
    );
  }

  return (
    <>
      <AgentComponent
        onSend={onSendRequest ?? onSend}
        onStop={onStop}
        isBusy={isResponding}
        messages={translatedMessages}
        isSidebarOpen={isSidebarOpen}
        paymentErrorMessage={paymentErrorMessage}
        agentId={agentId}
        chatId={chatId}
        ref={agentRef}
        onUpgrade={onUpgrade || (() => setIsUpgradeModalOpen(true))}
        onAttachmentClick={onAttachmentClick}
        inputContent={inputContent}
        onInputContentChange={onInputContentChange}
        pendingFiles={pendingFiles}
        onPendingFilesChange={onPendingFilesChange}
        showAttachments={showAttachments}
        scrollToBottomOffsetRem={scrollToBottomOffsetRem}

        hasMoreOlder={hasMoreOlder}
        isLoadingOlder={isLoadingOlder}
        onLoadOlder={loadOlder}

        onRollbackMessage={requestRollbackConfirmation}
        rollbackingMessageId={rollbackingMessageId}
      />
      <RollbackConfirmModal
        open={Boolean(pendingRollbackMessageId)}
        onClose={closeRollbackModal}
        onConfirm={confirmRollback}
        isLoading={Boolean(rollbackingMessageId)}
        error={rollbackModalError}
      />
      <AgentUpgradeModal
        appId={agentId}
        isOpen={isUpgradeModalOpen}
        onClose={handleUpgradeClose}
      />
    </>
  );
});

export default MyAgent;


